import React, { useEffect } from 'react';

const ImageAnnotationModal = ({
    // Core props
    selectedImage,
    onClose,
    
    // Annotation data
    annotations,
    labels,
    
    // Canvas refs
    canvasRef,
    imageRef,
    
    // Annotation state
    isAnnotating,
    selectedLabel,
    setIsAnnotating,
    setSelectedLabel,
    
    // Auto-label props
    currentImageAutolabelSettings,
    setCurrentImageAutolabelSettings,
    pendingAutolabels,
    autolabelLoading,
    autolabelImage,
    saveAutolabels,
    discardAutolabels,
    
    // Navigation props
    currentIndex,
    gotoPrevImage,
    gotoNextImage,
    filteredImages,
    images,
    
    // Canvas handlers
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    drawAnnotations,
    
    // Utility functions
    getImageUrl,
    deleteAnnotation,
    relabelAnnotation
}) => {
    // Redraw annotations when they change or when the image changes
    useEffect(() => {
        if (!selectedImage) return;
        
        const canvas = canvasRef.current;
        const img = imageRef.current;
        if (canvas && img && img.complete && img.offsetWidth > 0) {
            // Ensure canvas is properly sized
            canvas.style.width = img.offsetWidth + 'px';
            canvas.style.height = img.offsetHeight + 'px';
            
            const devicePixelRatio = window.devicePixelRatio || 1;
            canvas.width = img.offsetWidth * devicePixelRatio;
            canvas.height = img.offsetHeight * devicePixelRatio;
            
            const ctx = canvas.getContext('2d');
            ctx.scale(devicePixelRatio, devicePixelRatio);
            
            drawAnnotations();
        }
    }, [annotations, selectedImage, drawAnnotations, canvasRef, imageRef]);

    // Handle window resize
    useEffect(() => {
        if (!selectedImage) return;

        const handleResize = () => {
            const canvas = canvasRef.current;
            const img = imageRef.current;
            if (canvas && img && img.complete && img.offsetWidth > 0) {
                requestAnimationFrame(() => {
                    canvas.style.width = img.offsetWidth + 'px';
                    canvas.style.height = img.offsetHeight + 'px';
                    
                    const devicePixelRatio = window.devicePixelRatio || 1;
                    canvas.width = img.offsetWidth * devicePixelRatio;
                    canvas.height = img.offsetHeight * devicePixelRatio;
                    
                    const ctx = canvas.getContext('2d');
                    ctx.scale(devicePixelRatio, devicePixelRatio);
                    
                    drawAnnotations();
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [selectedImage, drawAnnotations, canvasRef, imageRef]);

    if (!selectedImage) return null;

    return (
        <div className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-2 md:p-4">
            <div className="relative w-full max-w-7xl h-[98vh] md:h-[95vh] bg-gradient-to-br from-[#0a1628] to-[#1a2448] rounded-xl border border-[#4cc3e9]/30 overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-4 border-b border-white/10 flex flex-col gap-3 md:flex-row md:items-center md:justify-between flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-white">
                            {selectedImage.s3_key.split('/').pop()}
                        </h3>
                        <div className="text-sm text-white/60">
                            {selectedImage.width_px} × {selectedImage.height_px} • {annotations.length} annotations
                        </div>
                    </div>
                    <div className="w-full md:w-auto flex flex-wrap items-center gap-2 md:justify-end">
                        {/* Autolabel Settings */}
                        <div className="flex items-center gap-2 px-3 py-1 rounded bg-white/5 border border-white/10">
                            <span className="text-xs text-white/70">Auto-Label:</span>
                            <select
                                value={currentImageAutolabelSettings.type}
                                onChange={(e) => setCurrentImageAutolabelSettings(prev => ({ ...prev, type: e.target.value }))}
                                className="px-2 py-1 text-xs rounded bg-white/10 border border-white/20 text-white [&>option]:bg-gray-800 [&>option]:text-white"
                            >
                                <option value="series" className="bg-gray-800 text-white">Series</option>
                                <option value="matches" className="bg-gray-800 text-white">Matches</option>
                            </select>
                            <input
                                type="number"
                                min="0"
                                max="100"
                                value={currentImageAutolabelSettings.minConfidence}
                                onChange={(e) => setCurrentImageAutolabelSettings(prev => ({ ...prev, minConfidence: Number(e.target.value) }))}
                                className="w-16 px-2 py-1 text-xs rounded bg-white/10 border border-white/20 text-white"
                                placeholder="80"
                            />
                            <span className="text-xs text-white/50">%</span>
                        </div>
                        
                        <select
                            value={selectedLabel}
                            onChange={(e) => setSelectedLabel(e.target.value)}
                            className="px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-sm [&>option]:bg-gray-800 [&>option]:text-white"
                            disabled={labels.length === 0}
                        >
                            <option value="" className="bg-gray-800 text-white">Select label to annotate</option>
                            {labels.map(label => (
                                <option key={label.id} value={label.id} className="bg-gray-800 text-white">{label.name}</option>
                            ))}
                        </select>
                        {pendingAutolabels[selectedImage.id] ? (
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => saveAutolabels(selectedImage.id)}
                                    disabled={autolabelLoading[selectedImage.id]}
                                    className="px-3 py-1.5 rounded text-sm font-medium bg-green-600 hover:bg-green-700 disabled:bg-green-600/50 text-white transition-colors"
                                >
                                    Save Auto-Labels ({pendingAutolabels[selectedImage.id].annotations.length})
                                </button>
                                <button
                                    onClick={() => discardAutolabels(selectedImage.id)}
                                    className="px-3 py-1.5 rounded text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors"
                                >
                                    Discard
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => autolabelImage(selectedImage)}
                                disabled={autolabelLoading[selectedImage.id]}
                                className="px-3 py-1.5 rounded text-sm font-medium bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 text-white transition-colors flex items-center gap-2"
                            >
                                {autolabelLoading[selectedImage.id] ? (
                                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                )}
                                Auto-Label
                            </button>
                        )}
                        {/* Navigation controls */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={gotoPrevImage}
                                disabled={currentIndex <= 0}
                                className="px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-sm disabled:opacity-50"
                                title="Previous image"
                            >
                                ◀ Prev
                            </button>
                            <button
                                onClick={gotoNextImage}
                                disabled={(() => {
                                    const list = filteredImages || images;
                                    return currentIndex < 0 || !list || currentIndex >= list.length - 1;
                                })()}
                                className="px-3 py-1.5 rounded bg-white/10 border border-white/20 text-white text-sm disabled:opacity-50"
                                title="Next image"
                            >
                                Next ▶
                            </button>
                        </div>
                        <button
                            onClick={() => {
                                setIsAnnotating(!isAnnotating);
                                setSelectedLabel('');
                            }}
                            disabled={labels.length === 0}
                            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                isAnnotating
                                    ? 'bg-red-600 hover:bg-red-700 text-white'
                                    : 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-600 disabled:cursor-not-allowed'
                            }`}
                        >
                            {isAnnotating ? 'Stop Annotating' : 'Start Annotating'}
                        </button>
                        <button
                            onClick={onClose}
                            className="px-3 py-1.5 rounded bg-gray-600 hover:bg-gray-700 text-white text-sm"
                        >
                            Close
                        </button>
                    </div>
                </div>

                <div className="flex flex-1 min-h-0">
                    {/* Image Canvas */}
                    <div className="flex-1 relative bg-gray-900/50 flex items-center justify-center p-4 overflow-hidden min-w-0">
                        <div className="relative w-full h-full flex items-center justify-center">
                            <div className="relative">
                                <img
                                    ref={imageRef}
                                    src={getImageUrl(selectedImage)}
                                    alt="Annotation"
                                    className="max-w-full max-h-full object-contain block"
                                    onLoad={() => {
                                        const canvas = canvasRef.current;
                                        const img = imageRef.current;
                                        if (canvas && img) {
                                            // Wait for next frame to ensure image is fully rendered
                                            requestAnimationFrame(() => {
                                                // Set canvas display size to match image display size exactly
                                                canvas.style.width = img.offsetWidth + 'px';
                                                canvas.style.height = img.offsetHeight + 'px';
                                                
                                                // Set canvas internal resolution to match display size for crisp rendering
                                                const devicePixelRatio = window.devicePixelRatio || 1;
                                                canvas.width = img.offsetWidth * devicePixelRatio;
                                                canvas.height = img.offsetHeight * devicePixelRatio;
                                                
                                                // Scale the context to account for device pixel ratio
                                                const ctx = canvas.getContext('2d');
                                                ctx.scale(devicePixelRatio, devicePixelRatio);
                                                
                                                // Redraw annotations after canvas is properly sized
                                                drawAnnotations();
                                            });
                                        }
                                    }}
                                />
                                <canvas
                                    ref={canvasRef}
                                    className="absolute top-0 left-0 cursor-crosshair pointer-events-auto"
                                    onMouseDown={handleMouseDown}
                                    onMouseMove={handleMouseMove}
                                    onMouseUp={handleMouseUp}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Annotations List */}
                    <div className="w-80 lg:w-80 md:w-72 sm:w-64 border-l border-white/10 p-4 overflow-y-auto flex-shrink-0 min-w-0">
                        <h4 className="text-sm font-semibold text-white mb-3">Annotations ({annotations.length})</h4>
                        {annotations.length === 0 ? (
                            <div className="text-white/60 text-sm">No annotations yet</div>
                        ) : (
                            <div className="space-y-2">
                                {annotations.map((annotation, index) => {
                                    const label = labels.find(l => l.id === annotation.label_id);
                                    const color = `hsl(${(index * 60) % 360}, 70%, 50%)`;
                                    
                                    return (
                                        <div
                                            key={annotation.id}
                                            className="flex items-center justify-between p-2 rounded bg-white/5 border border-white/10"
                                        >
                                            <div className="flex items-center gap-2">
                                                <div
                                                    className="w-3 h-3 rounded"
                                                    style={{ backgroundColor: color }}
                                                ></div>
                                                <span className="text-sm text-white">{label?.name || 'Unknown'}</span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => relabelAnnotation(annotation)}
                                                    className="text-blue-400 hover:text-blue-300 text-xs"
                                                    title="Change label"
                                                >
                                                    Relabel
                                                </button>
                                                <button
                                                    onClick={() => deleteAnnotation(annotation.id)}
                                                    className="text-red-400 hover:text-red-300 text-xs"
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                        
                        {labels.length === 0 && (
                            <div className="mt-4 p-3 rounded bg-yellow-500/10 border border-yellow-500/20">
                                <div className="text-yellow-300 text-sm">
                                    No labels available. Create labels first to start annotating.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageAnnotationModal;
